import React, { useEffect, useState } from 'react';
import { Box, Grid2 } from '@mui/material';
import CustomDialog from '@ui-components/Dialog/CustomDialog';
import CustomDialogContent from '@ui-components/Dialog/CustomDialogContent';
import CustomDialogActions from '@ui-components/Dialog/CustomDialogActions';
import CustomButton from '@ui-components/Button/CustomButton';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import SearchIcon from '@assets/images/search.svg';
import CustomTable from '@ui-components/Table/CustomTable';

import CustomTableCell from '@ui-components/Table/CustomTableCell';
import CustomTableHead from '@ui-components/Table/CustomTableHead';
import CustomTableRow from '@ui-components/Table/CustomTableRow';
import CustomTableBody from '@ui-components/Table/CustomTableBody';
import CustomCheckBox from '@ui-components/CheckBox/CustomCheckBox';
import { useNavigate } from 'react-router';
import type { AzureEntraUsers } from '../../API/services/ClinicianService';

interface FetchEntraUsersProps {
  openAzureEntraUsers: boolean;
  onAzureEntraClose: () => void;
  azureEntraUsers: AzureEntraUsers[];
  isLoadingAzureEntraUsers: boolean;
  path: string;
  title: string;
}

const FetchEntraUsers: React.FC<FetchEntraUsersProps> = ({
  openAzureEntraUsers,
  onAzureEntraClose,
  azureEntraUsers,
  isLoadingAzureEntraUsers,
  path,
  title,
}) => {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<AzureEntraUsers | null>(
    null
  );

  const [searchTermAzureEntraUsers, setSearchTermAzureEntraUsers] =
    useState<string>('');

  const handleAzureEntraSearch = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchTermAzureEntraUsers(event.target.value);
  };

  const filteredAzureEntraUsers = azureEntraUsers?.filter((user) => {
    const trimmedSearchTerm = searchTermAzureEntraUsers.trim().toLowerCase();
    return (
      trimmedSearchTerm === '' ||
      (user.email?.toLowerCase().includes(trimmedSearchTerm) ?? false) ||
      (user.firstName?.toLowerCase().includes(trimmedSearchTerm) ?? false) ||
      (user.lastName?.toLowerCase().includes(trimmedSearchTerm) ?? false)
    );
  });

  const handleSelectAzureEntraUser = (user: AzureEntraUsers) => {
    setSelectedUser(user);
  };

  const handleDeselectAzureEntraUser = () => {
    setSelectedUser(null);
  };

  useEffect(() => {
    if (openAzureEntraUsers) {
      setSearchTermAzureEntraUsers('');
      setErrorMessage('');
    }
  }, [openAzureEntraUsers]);

  return (
    <Box>
      <CustomDialog
        id="Azure-user"
        maxWidth="md"
        disableEscapeKeyDown
        open={openAzureEntraUsers}
        onClose={() => {
          onAzureEntraClose();
        }}
      >
        <CustomDialogContent className="add-clinician">
          <Box className="pb-8">
            <CustomTypography id="add-clinician-user-title" variant="h4">
              Add {title}
            </CustomTypography>
          </Box>
          <Box>
            <CustomTypography
              id="add-clinician-text"
              variant="body3"
              color="textDisabled"
            >
              Search for an existing {title.toLocaleLowerCase()} by first name,
              last name, or email.
            </CustomTypography>
          </Box>

          <Box className="mt-32 mb-8">
            <CustomTextField
              id="txt-search-azure-user"
              placeholder="Type to search"
              variant="outlined"
              size="small"
              type="text"
              value={searchTermAzureEntraUsers}
              onChange={handleAzureEntraSearch}
              endAdornment={
                <CustomIconButton
                  id="search-icon"
                  size="small"
                  icon={<SearchIcon />}
                />
              }
            />
          </Box>
          <Box className="pt-20">
            {errorMessage && (
              <CustomTypography
                id="error-message"
                color="error"
                variant="body2"
                className="pb-8"
              >
                {errorMessage}
              </CustomTypography>
            )}
            {isLoadingAzureEntraUsers ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <CustomTypography
                  id="loading-text"
                  variant="h5"
                  className="text-align-center pt-32"
                  color="textDisabled"
                >
                  Loading...
                </CustomTypography>
              </Box>
            ) : (
              <CustomTable
                stickyHeader={true}
                tableContainerClassName="table-container-for-popup"
                id="table-clinicians-list"
              >
                <CustomTableHead id="table-head-clinicians-list">
                  <CustomTableRow id="clinicians-user-header">
                    <CustomTableCell id="table-cell-select">
                      SELECT
                    </CustomTableCell>
                    <CustomTableCell id="table-cell-first-name">
                      FIRST NAME&nbsp;
                    </CustomTableCell>
                    <CustomTableCell id="table-cell-last-name">
                      LAST NAME&nbsp;
                    </CustomTableCell>
                    <CustomTableCell id="table-cell-primary-email">
                      PRIMARY EMAIL &nbsp;
                    </CustomTableCell>
                  </CustomTableRow>
                </CustomTableHead>
                {filteredAzureEntraUsers &&
                filteredAzureEntraUsers?.length > 0 ? (
                  <CustomTableBody id="table-body-partner">
                    {filteredAzureEntraUsers?.map((user) => (
                      <CustomTableRow
                        key={user.email}
                        id={`table-row-azure-user-${user.email}`}
                      >
                        <CustomTableCell
                          id={`table-cell-clinician-id-${user.email}`}
                        >
                          <CustomCheckBox
                            id={`checkbox-clinician-id-${user.email}`}
                            checked={selectedUser?.email === user.email}
                            onChange={(e) => {
                              if (e.target.checked) {
                                handleSelectAzureEntraUser(user);
                              } else {
                                handleDeselectAzureEntraUser();
                              }
                              setErrorMessage(null);
                            }}
                          />
                        </CustomTableCell>

                        <CustomTableCell
                          id={`table-cell-clinician-first-name-${user.firstName}`}
                        >
                          {user.firstName}
                        </CustomTableCell>
                        <CustomTableCell
                          id={`table-cell-clinician-last-name-${user.lastName}`}
                        >
                          {user.lastName}
                        </CustomTableCell>

                        <CustomTableCell
                          id={`table-cell-clinician-email-${user.email}`}
                        >
                          {user.email}
                        </CustomTableCell>
                      </CustomTableRow>
                    ))}
                  </CustomTableBody>
                ) : (
                  <CustomTableBody id="table-body-clinician">
                    <CustomTableRow id="table-row-clinician">
                      <CustomTableCell id="table-cell-clinician-id" colspan={7}>
                        <Box className="flex flex-justify-center align-items-center">
                          <CustomTypography
                            id="no-entra-users-found"
                            variant="body2"
                            color="textDisabled"
                          >
                            No Entra Users Found
                          </CustomTypography>
                        </Box>
                      </CustomTableCell>
                    </CustomTableRow>
                  </CustomTableBody>
                )}
              </CustomTable>
            )}
          </Box>
        </CustomDialogContent>

        <CustomDialogActions>
          <Grid2 container className="pt-24">
            <Grid2 className="mr-8">
              <CustomButton
                id="btnYes"
                color="primary"
                variant="outlined"
                onClick={() => {
                  onAzureEntraClose();
                  setErrorMessage(null);
                  setSelectedUser(null);
                  setSearchTermAzureEntraUsers('');
                }}
              >
                Cancel
              </CustomButton>
            </Grid2>
            <Grid2>
              <CustomButton
                id="btnConfirm-complete"
                color="primary"
                variant="contained"
                onClick={() => {
                  if (selectedUser) {
                    setErrorMessage(null);
                    setSearchTermAzureEntraUsers('');

                    navigate(path, {
                      replace: true,
                      state: {
                        firstName: selectedUser.firstName,
                        lastName: selectedUser.lastName,
                        email: selectedUser.email,
                      },
                    });
                  } else {
                    setErrorMessage('Please select an email');
                  }
                }}
              >
                Confirm
              </CustomButton>
            </Grid2>
          </Grid2>
        </CustomDialogActions>
      </CustomDialog>
    </Box>
  );
};

export default FetchEntraUsers;
